import redirectInterceptor from "@/interceptors/redirect_interceptor";
import { weatherRoutingMapModule } from "./map";
import { weatherRoutingFormModule } from "./form";
import { weatherRoutingSpecialZonesModule } from "./special_zones";
import { weatherRoutingVoyagesModule } from "./past_voyages";
import { weatherRoutingVoyageDetailsModule } from "./voyage_details";
import { weatherRoutingRoutesModule } from "./routes";
import { weatherRoutingCreateRouteModule } from "./create_route";
import { weatherRoutingCustomAreasModule } from "./custom_areas";
import { weatherRoutingCreateCustomAreaModule } from "./create_custom_area";
import { weatherRoutingTilesModule } from "./tiles";
import { weatherRoutingPortModule } from "./port";
import vesselDataFromJson from "../../domain/weather_routing/vessel/mapper";
import WeatherRoutingCompanyService from "../../services/company/service";
import companyDataFromJson from "../../domain/weather_routing/company/mapper";
import WeatherRoutingAuthenticationService from "../../services/authentication/service";
import { onConfig } from "@/config";

const localWeatherRoutingServerUrl = process.env.VUE_APP_LOCAL_WR_API_URL;

let config = null;
let basicUrl = "";
let companyId = null;
let companyService = null;

onConfig((c) => {
  config = c;
  basicUrl = c.VUE_APP_BASIC_URL;
  companyId = c.VUE_APP_COMPANY_ID;

  companyService = new WeatherRoutingCompanyService(
    localWeatherRoutingServerUrl,
    companyId,
    companyDataFromJson
  );
});

const authenticationService = new WeatherRoutingAuthenticationService(
  localWeatherRoutingServerUrl
);

const state = {
  theyrBaseUrl: "https://api.t-vos.com/v1.20",
  weatherRoutingAccessToken: localStorage.getItem("theyr_access_token"),
  weatherRoutingRefreshToken: localStorage.getItem("theyr_refresh_token"),
  vesselParticulars: null,
  currentVesselCoordinates: null,
  vessels: [],
  company: null,
  fetchInitialDataLoading: true,
  fetchInitialDataError: null,
  fetchVesselDataLoading: true,
  fetchVesselDataError: null,
  selectedVessel: null,

  authenticationLoading: true,
  authenticationError: null,

};

const getters = {
  getVesselParticulars: (state) => (state.vesselParticulars),
  getTrimValues: (state) => ([...new Set(state.vessels.map((vessel) => vessel.modelTrim))]),
  getFoulingValues: (state) => ([...new Set(state.vessels.map((vessel) => vessel.modelFouling))]),
  remainingComputationTokens: (state) => {
    if (state.company !== null) {
      return state.company.remainingTokens;
    }
  },
  showComputationWarning: (state) => {
    if (state.company !== null) {
      const totalTokens = state.company.computationTokens;
      const remainingTokens = state.company.remainingTokens;

      const threshold = 0.1 * totalTokens;

      return remainingTokens <= threshold;
    }
  },
  noTokensRemaining: (state) => {
    if (state.company !== null) {
      return state.company.remainingTokens === 0;
    }
  },
};

const actions = {

  async postAuthentication({ commit }) {
    commit("SET_AUTHENTICATION_LOADING_STATE");
    try {
      await authenticationService.authenticate();
      commit("SET_WEATHER_ROUTING_ACCESS_TOKEN", localStorage.getItem("theyr_access_token"));
      commit("SET_WEATHER_ROUTING_REFRESH_TOKEN", localStorage.getItem("theyr_refresh_token"));
      commit("SET_AUTHENTICATION_SUCCESS_STATE");
    } catch (error) {
      commit("SET_AUTHENTICATION_ERROR_STATE", error);
    }
  },

  async fetchVesselParticulars({ commit }, vesselParticularsPaylooad) {

    const vesselName = vesselParticularsPaylooad.vesselName;
    const response = await redirectInterceptor.get(
      `${basicUrl}/weather-routing/vessel/${vesselName}/vessel-particulars`,
      {
        headers: {
          Authorization: localStorage.getItem("quad_token")
        }
      }
    );
    if (response.status >= 200 && response.status < 400) {
      commit("SET_VESSEL_PARTICULARS", response.data);
    }
    else {
      throw new Error(response.data.error);
    }
  },

  async fetchCurrentVesselCoordinates({ commit }, vesselParticularsPaylooad) {

    const vesselName = vesselParticularsPaylooad.vesselName;
    const response = await redirectInterceptor.get(
      `${basicUrl}/weather-routing/vessel/${vesselName}/coordinates`,
      {
        headers: {
          Authorization: localStorage.getItem("quad_token")
        }
      }
    );

    if (response.status >= 200 && response.status < 400) {
      commit("SET_CURRENT_VESSEL_COORDINATES", response.data);
    }
    else {
      throw new Error(response.data.error);
    }
  },

  async fetchVessels({ commit }) {
    const response = await redirectInterceptor.get(
      `${localWeatherRoutingServerUrl}/vessel`,
      {
        headers: {
          "theyr-token": `${state.weatherRoutingAccessToken}`,
          "access-token": localStorage.getItem("quad_token"),
        },
        params: {
          company_id: companyId,
          imo_identifier: state.vesselParticulars.IMO
        }
      }
    );

    if (response.status >= 200 && response.status < 400) {
      const vessels = response.data.map((datum) => vesselDataFromJson(datum));
      commit("SET_VESSELS", vessels);
    }
    else {
      throw new Error(response.data.error);
    }
  },

  async fetchCompany({ commit }) {
    const accessToken = localStorage.getItem("quad_token");
    try {
      const company = await companyService.getOne(accessToken);
      commit("SET_COMPANY", company);
    }
    catch (error) {
      // handle error case
    }
  },

  async fetchInitialData({ commit, dispatch }) {
    commit("SET_FETCH_INITIAL_DATA_LOADING_STATE");
    try {
      if (state.company === null) {
        await dispatch("fetchCompany");
      }
      await Promise.all([
        dispatch("voyagesModule/fetchAllVoyages"),
        dispatch("routesModule/fetchAllRoutes"),
        dispatch("customAreasModule/fetchAllAreas"),
        dispatch("fetchVessels"),
        dispatch("portModule/fetchPortsData"),
      ]);
      commit("SET_FETCH_INITIAL_DATA_SUCCESS_STATE");
    } catch (error) {
      commit("SET_FETCH_INITIAL_DATA_ERROR_STATE", error);
    }
  },

  async fetchVesselData({ commit, dispatch }, vesselName) {
    commit("SET_FETCH_VESSEL_DATA_LOADING_STATE");
    try {
      dispatch("_resetStates");
      await Promise.all([
        dispatch("fetchVesselParticulars", {
          vesselName: vesselName,
        }),
        dispatch(
          "formModule/fetchCurrentVesselMetrics",
          {
            vesselName: vesselName,
          }
        ),
        dispatch(
          "fetchCurrentVesselCoordinates",
          {
            vesselName: vesselName,
          }
        ),
      ]);
      commit("SET_FETCH_VESSEL_DATA_SUCCESS_STATE");
    }
    catch (error) {
      commit("SET_FETCH_VESSEL_DATA_ERROR_STATE", error);
    }
  },

  _resetStates({ commit }) {
    commit("formModule/RESET_STATE");
    commit("customAreasModule/RESET_STATE");
    commit("routesModule/RESET_STATE");
    commit("voyageDetailsModule/RESET_STATE");
    commit("weatherRoutingMapModule/RESET_STATE");
  }

};

const mutations = {
  SET_WEATHER_ROUTING_ACCESS_TOKEN: (state, token) => (state.weatherRoutingAccessToken = token),
  SET_WEATHER_ROUTING_REFRESH_TOKEN: (state, token) => (state.weatherRoutingRefreshToken = token),
  SET_VESSEL_PARTICULARS: (state, data) => (state.vesselParticulars = data),
  SET_CURRENT_VESSEL_COORDINATES: (state, data) => (state.currentVesselCoordinates = data),
  SET_VESSELS: (state, data) => (state.vessels = data),
  SET_COMPANY: (state, data) => (state.company = data),
  SET_SELECTED_VESSEL: (state, data) => (state.selectedVessel = data),
  SET_FETCH_INITIAL_DATA_LOADING_STATE: (state) => {
    state.fetchInitialDataLoading = true;
    state.fetchInitialDataError = null;
  },
  SET_FETCH_INITIAL_DATA_ERROR_STATE: (state, error) => {
    state.fetchInitialDataError = error;
    state.fetchInitialDataLoading = false;
  },
  SET_FETCH_INITIAL_DATA_SUCCESS_STATE: (state) => {
    state.fetchInitialDataLoading = false;
    state.fetchInitialDataError = null;
  },
  SET_FETCH_VESSEL_DATA_LOADING_STATE: (state) => {
    state.fetchVesselDataLoading = true;
    state.fetchVesselDataError = null;
  },
  SET_FETCH_VESSEL_DATA_ERROR_STATE: (state, error) => {
    state.fetchVesselDataError = error;
    state.fetchVesselDataLoading = false;
  },
  SET_FETCH_VESSEL_DATA_SUCCESS_STATE: (state) => {
    state.fetchVesselDataLoading = false;
    state.fetchVesselDataError = null;
  },
  SET_AUTHENTICATION_LOADING_STATE: (state) => {
    state.authenticationLoading = true;
    state.authenticationError = null;
  },
  SET_AUTHENTICATION_ERROR_STATE: (state, error) => {
    state.authenticationError = error;
    state.authenticationLoading = false;
  },
  SET_AUTHENTICATION_SUCCESS_STATE: (state) => {
    state.authenticationLoading = false;
    state.authenticationError = null;
  }
};

export const weatherRoutingModule = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
  modules: {
    weatherRoutingMapModule: weatherRoutingMapModule,
    formModule: weatherRoutingFormModule,
    portModule: weatherRoutingPortModule,
    voyagesModule: weatherRoutingVoyagesModule,
    routesModule: weatherRoutingRoutesModule,
    customAreasModule: weatherRoutingCustomAreasModule,
    createRouteModule: weatherRoutingCreateRouteModule,
    createCustomAreaModule: weatherRoutingCreateCustomAreaModule,
    voyageDetailsModule: weatherRoutingVoyageDetailsModule,
    specialZones: weatherRoutingSpecialZonesModule,
    tilesModule: weatherRoutingTilesModule
  }
};
